import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountSettings = ({ user, onUpdateProfile, onUpdatePreferences }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [preferences, setPreferences] = useState({
    emailNotifications: user?.preferences?.emailNotifications || true,
    smsNotifications: user?.preferences?.smsNotifications || false,
    marketingEmails: user?.preferences?.marketingEmails || false,
    weeklyDigest: user?.preferences?.weeklyDigest || true
  });

  const sections = [
    {
      id: 'profile',
      title: 'Profile Information',
      icon: 'User',
      description: 'Manage your personal information'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: 'Bell',
      description: 'Configure your notification preferences'
    },
    {
      id: 'billing',
      title: 'Billing & Subscription',
      icon: 'CreditCard',
      description: 'Manage your subscription and billing'
    },
    {
      id: 'security',
      title: 'Security',
      icon: 'Shield',
      description: 'Password and security settings'
    }
  ];

  const handleProfileUpdate = () => {
    if (onUpdateProfile) {
      onUpdateProfile(profileData);
    }
  };

  const handlePreferencesUpdate = () => {
    if (onUpdatePreferences) {
      onUpdatePreferences(preferences);
    }
  };

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="First Name"
          type="text"
          value={profileData?.firstName}
          onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e?.target?.value }))}
          placeholder="Enter your first name"
        />
        <Input
          label="Last Name"
          type="text"
          value={profileData?.lastName}
          onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e?.target?.value }))}
          placeholder="Enter your last name"
        />
      </div>
      
      <Input
        label="Email Address"
        type="email"
        value={profileData?.email}
        onChange={(e) => setProfileData(prev => ({ ...prev, email: e?.target?.value }))}
        placeholder="Enter your email"
        description="This email will be used for account notifications"
      />
      
      <Input
        label="Phone Number"
        type="tel"
        value={profileData?.phone}
        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e?.target?.value }))}
        placeholder="Enter your phone number"
      />
      
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handleProfileUpdate}
          iconName="Save"
          iconPosition="left"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <Checkbox
          label="Email Notifications"
          description="Receive notifications about your planners via email"
          checked={preferences?.emailNotifications}
          onChange={(e) => setPreferences(prev => ({ ...prev, emailNotifications: e?.target?.checked }))}
        />
        
        <Checkbox
          label="SMS Notifications"
          description="Receive important updates via text message"
          checked={preferences?.smsNotifications}
          onChange={(e) => setPreferences(prev => ({ ...prev, smsNotifications: e?.target?.checked }))}
        />
        
        <Checkbox
          label="Marketing Emails"
          description="Receive promotional content and feature updates"
          checked={preferences?.marketingEmails}
          onChange={(e) => setPreferences(prev => ({ ...prev, marketingEmails: e?.target?.checked }))}
        />
        
        <Checkbox
          label="Weekly Digest"
          description="Get a weekly summary of your planner activity"
          checked={preferences?.weeklyDigest}
          onChange={(e) => setPreferences(prev => ({ ...prev, weeklyDigest: e?.target?.checked }))}
        />
      </div>
      
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={handlePreferencesUpdate}
          iconName="Save"
          iconPosition="left"
        >
          Save Preferences
        </Button>
      </div>
    </div>
  );

  const renderBillingSection = () => (
    <div className="space-y-6">
      <div className="bg-muted rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-heading font-semibold text-foreground">Current Plan</h4>
            <p className="font-body text-sm text-muted-foreground">Free Plan</p>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold text-foreground">$0</div>
            <div className="font-caption text-xs text-muted-foreground">per month</div>
          </div>
        </div>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-success" />
            <span className="font-body text-sm text-foreground">Up to 5 planners per month</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-success" />
            <span className="font-body text-sm text-foreground">Basic templates</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="X" size={16} className="text-muted-foreground" />
            <span className="font-body text-sm text-muted-foreground">Premium templates</span>
          </div>
        </div>
        
        <Button variant="outline" fullWidth>
          Upgrade to Pro
        </Button>
      </div>
      
      <div className="border border-border rounded-lg p-4">
        <h4 className="font-heading font-semibold text-foreground mb-2">Billing History</h4>
        <p className="font-body text-sm text-muted-foreground">No billing history available</p>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-heading font-semibold text-foreground">Password</h4>
              <p className="font-body text-sm text-muted-foreground">Last updated 30 days ago</p>
            </div>
            <Button variant="outline" size="sm">
              Change Password
            </Button>
          </div>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-heading font-semibold text-foreground">Two-Factor Authentication</h4>
              <p className="font-body text-sm text-muted-foreground">Add an extra layer of security</p>
            </div>
            <Button variant="outline" size="sm">
              Enable 2FA
            </Button>
          </div>
        </div>
        
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-heading font-semibold text-foreground">Active Sessions</h4>
              <p className="font-body text-sm text-muted-foreground">Manage your active login sessions</p>
            </div>
            <Button variant="outline" size="sm">
              View Sessions
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'billing':
        return renderBillingSection();
      case 'security':
        return renderSecuritySection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-6">
        <h2 className="font-heading text-xl font-semibold text-foreground">
          Account Settings
        </h2>
        <p className="font-body text-sm text-muted-foreground mt-1">
          Manage your account preferences and settings
        </p>
      </div>
      <div className="flex">
        {/* Settings Navigation */}
        <div className="w-64 border-r border-border p-4">
          <nav className="space-y-2">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`
                  w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left
                  transition-colors duration-200
                  ${activeSection === section?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={section?.icon} size={16} />
                <div>
                  <div className="font-body font-medium text-sm">{section?.title}</div>
                  <div className="font-caption text-xs opacity-80">{section?.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Settings Content */}
        <div className="flex-1 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;